import type { KeyThemesProjection, ListeningState } from "../../shared/types.ts";

export interface CurrentPointView {
  text: string;
  speaker: string;
  subpoints: string[];
}

export type KeyThemesViewModel =
  | { kind: "loading" }
  | { kind: "qr" }
  | {
      kind: "stage";
      current: {
        lead: CurrentPointView;
        restCandidates: CurrentPointView[];
        timeLabel: string;
      } | null;
      themes: Array<{ key: string; title: string; summary: string | null }>;
      themesThroughLabel: string | null;
    };

const MAX_REST_POINTS = 2;
/** Upper bound on the candidate lines handed to the stage, main points and
 *  subpoints combined. The stage measures the real panel and shrinks the
 *  visible budget until whole lines fit — see allocateLiveLines. */
export const MAX_LIVE_LINES = 16;

export function deriveKeyThemesView(
  view: KeyThemesProjection | null | undefined,
): KeyThemesViewModel {
  if (view === undefined) return { kind: "loading" };
  if (
    !view ||
    (view.currentSections.length === 0 && view.themes.length === 0 && !view.currentDiscussion)
  ) {
    return { kind: "qr" };
  }

  // Episode pipeline: the derived bundle is the live surface — prefer it.
  if (view.currentDiscussion) {
    const bundle = view.currentDiscussion;
    const speaker = bundle.speaker ?? view.currentSections.at(-1)?.speaker ?? "";
    // The lead is the claim's mainPoint — the exact wording the timeline
    // shows — so the projector and the timeline never diverge. The episode
    // headline is stored but not displayed. Extensions accumulate subpoints
    // on the timeline without bound; hand the stage the newest candidates
    // and let the height-fit pass trim them.
    const subpoints = bundle.subpoints.slice(-(MAX_LIVE_LINES - 1));
    return {
      kind: "stage",
      current: {
        lead: { text: bundle.mainPoint, speaker, subpoints },
        restCandidates: bundle.previous
          ? [{ text: bundle.previous.mainPoint, speaker: "", subpoints: [] }]
          : [],
        timeLabel: formatClock(bundle.lastChangedAt),
      },
      themes: view.themes.map((theme, index) => ({
        key: theme.themeId ?? `theme-${index}`,
        title: theme.title,
        summary: theme.summary,
      })),
      themesThroughLabel: view.themesConsolidatedAt !== null
        ? formatClock(view.themesConsolidatedAt)
        : null,
    };
  }

  const points: Array<CurrentPointView & { timeLabel: string }> = [];
  for (const section of view.currentSections) {
    for (const point of section.points) {
      points.push({
        text: point.text,
        speaker: section.speaker,
        subpoints: point.subpoints,
        timeLabel: section.timeLabel,
      });
    }
  }
  const lead = points.at(-1) ?? null;
  const restCandidates = points
    .slice(0, -1)
    .reverse()
    .slice(0, MAX_REST_POINTS)
    .map(({ text, speaker, subpoints }) => ({
      text,
      speaker,
      subpoints: subpoints.slice(-(MAX_LIVE_LINES - 1)),
    }));

  return {
    kind: "stage",
    current: lead
      ? {
          lead: {
            text: lead.text,
            speaker: lead.speaker,
            subpoints: lead.subpoints.slice(-(MAX_LIVE_LINES - 1)),
          },
          restCandidates,
          timeLabel: lead.timeLabel,
        }
      : null,
    themes: view.themes.map((theme, index) => ({
      key: theme.themeId ?? `theme-${index}`,
      title: theme.title,
      summary: theme.summary,
    })),
    themesThroughLabel: view.themesConsolidatedAt !== null
      ? formatClock(view.themesConsolidatedAt)
      : null,
  };
}

/** Freshness window for the live "Listening" indicator. */
export const LISTENING_FRESH_MS = 12_000;

/** Label for the live panel's listening indicator, or null when speech is
 *  stale. Unmapped diarization labels never reach the audience screen. */
export function deriveListeningLabel(
  listening: ListeningState | null | undefined,
  now: number,
): string | null {
  if (!listening || now - listening.lastEntryTs > LISTENING_FRESH_MS) return null;
  const name = listening.speaker.trim();
  const isRawDiarizationLabel = /^speaker[\s_-]?\d+$/i.test(name);
  return name && !isRawDiarizationLabel ? `Listening · ${name}` : "Listening…";
}

/** 12-hour clock label matching the backend's section timeLabel style ("7:58 PM"). */
export function formatClock(ts: number): string {
  return new Date(ts)
    .toLocaleTimeString([], { hour: "numeric", hour12: true, minute: "2-digit" })
    .toUpperCase();
}

/** Spend a line budget across the live panel, newest content first: the lead
 *  keeps its newest subpoints, leftovers go to earlier points (each costing
 *  one line plus its newest subpoints). The stage calls this with shrinking
 *  budgets until the allocation fits the panel height, so new material pushes
 *  the oldest lines out only when space actually runs out. */
export function allocateLiveLines(
  leadSubpoints: string[],
  restCandidates: CurrentPointView[],
  budget: number,
): { leadSubpoints: string[]; rest: CurrentPointView[] } {
  const kept = budget > 1 ? leadSubpoints.slice(-(budget - 1)) : [];
  let remaining = budget - 1 - kept.length;
  const rest: CurrentPointView[] = [];
  for (const point of restCandidates) {
    if (remaining < 1) break;
    const subpoints = remaining > 1 ? point.subpoints.slice(-(remaining - 1)) : [];
    rest.push({ text: point.text, speaker: point.speaker, subpoints });
    remaining -= 1 + subpoints.length;
  }
  return { leadSubpoints: kept, rest };
}
